import type { FieldProblem } from '@fpc/shared';

interface ErrorLike {
  status?: number;
  code?: string;
  message?: string;
  details?: unknown;
}

/** The field-by-field problems a validation failure carries, if any. */
export function problemsOf(error: unknown): FieldProblem[] {
  const details = (error as ErrorLike | null)?.details;
  if (!Array.isArray(details)) return [];
  return details
    .filter(
      (entry): entry is FieldProblem =>
        typeof entry?.path === 'string' && typeof entry?.message === 'string',
    )
    .map((entry) => ({ path: entry.path, message: entry.message }));
}

/** The form field a problem belongs to: `tdsProfiles.0.label` belongs to `tdsProfiles`. */
export function fieldOf(problem: FieldProblem): string {
  return problem.path.split('.')[0] ?? '';
}

/**
 * A name for a field the screen did not label itself, from its key:
 * `departmentIds` reads "Department", `poNumber` reads "Po number".
 */
export function fieldLabel(path: string): string {
  const words = path
    .split('.')[0]!
    .replace(/Ids?$/, '')
    .replace(/([a-z\d])([A-Z])/g, '$1 $2')
    .toLowerCase();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : 'This form';
}

/** The headline for a failure, in words the person at the keyboard can act on. */
export function plainErrorMessage(error: unknown): string {
  const apiError = error as ErrorLike | null;

  // fetch() itself failed: the API never answered.
  if (error instanceof TypeError || apiError?.status === undefined) {
    return apiError?.message && !/fetch|network/i.test(apiError.message)
      ? apiError.message
      : 'Cannot reach the server. Check your connection and try again.';
  }
  if (apiError.status === 403) {
    return 'You do not have permission to do this. Ask your administrator if you need access.';
  }
  if (apiError.status === 429) return 'Too many attempts. Wait a few minutes and try again.';
  if (apiError.status >= 500) {
    return 'Something went wrong on our side. Please try again in a moment.';
  }
  return apiError.message || 'That did not work. Please try again.';
}

/**
 * A failure as one line, for a spot with no room for a list: next to a save
 * button, or under a sign-in form. A validation failure names what to fix.
 */
export function describeError(error: unknown, labels?: Record<string, string>): string {
  const problems = problemsOf(error);
  if (!problems.length) return plainErrorMessage(error);
  return problems
    .map(
      (problem) => `${labels?.[fieldOf(problem)] ?? fieldLabel(problem.path)}: ${problem.message}`,
    )
    .join(' · ');
}
